import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DescriptionIcon from "@mui/icons-material/Description";
import KeyIcon from "@mui/icons-material/Key";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Zoom,
  Fade,
} from "@mui/material";
import axios from "../utils/api";

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 1400,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    mb: 4,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    "&:hover": {
      bgcolor: "#f5f5f5",
      transform: "scale(1.05)",
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.5rem", md: "2rem" },
    letterSpacing: "-0.5px",
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.95rem",
    mt: 0.5,
  },
  addButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    px: 3,
    py: 1.5,
    background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
    boxShadow: "0 8px 20px rgba(13, 71, 161, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #0a3d91 0%, #0d47a1 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 28px rgba(13, 71, 161, 0.4)",
    },
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#1e293b",
    mb: 3,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  },
  countBadge: {
    bgcolor: "#e2e8f0",
    color: "#475569",
    px: 2,
    py: 0.5,
    borderRadius: 999,
    fontSize: "0.875rem",
    fontWeight: 600,
    ml: 1,
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "20px",
    border: "1px solid rgba(0,0,0,0.06)",
    backgroundColor: "white",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 40px -12px rgba(13, 71, 161, 0.25)",
      borderColor: "rgba(13, 71, 161, 0.2)",
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: "linear-gradient(90deg, #0d47a1, #1565c0, #1976d2)",
      opacity: 0.8,
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: "24px",
  },
  cardKey: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    wordBreak: "break-word",
  },
  cardKeyIcon: {
    color: "#0d47a1",
    fontSize: "20px",
  },
  cardDetail: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#f8fafc",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
  },
  cardDetailIcon: {
    color: "#0d47a1",
    fontSize: "20px",
  },
  cardDetailContent: {
    flex: 1,
  },
  cardDetailLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "2px",
  },
  cardDetailValue: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1e293b",
  },
  cardDescription: {
    padding: "12px",
    backgroundColor: "#f1f5f9",
    borderRadius: "12px",
    fontSize: "0.9rem",
    color: "#475569",
    lineHeight: "1.5",
    marginBottom: "8px",
    border: "1px dashed #cbd5e1",
  },
  cardActions: {
    padding: "16px 24px 20px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
  },
  editButton: {
    flex: 1,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "8px 16px",
    color: "#0d47a1",
    border: "1px solid #0d47a1",
    backgroundColor: "transparent",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(13, 71, 161, 0.08)",
      borderColor: "#0d47a1",
      transform: "translateY(-2px)",
    },
  },
  deleteButton: {
    flex: 1,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "8px 16px",
    color: "#b91c1c",
    border: "1px solid #b91c1c",
    backgroundColor: "transparent",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(185, 28, 28, 0.08)",
      borderColor: "#b91c1c",
      transform: "translateY(-2px)",
    },
  },
  emptyState: {
    textAlign: "center",
    py: 8,
    px: 4,
    backgroundColor: "white",
    borderRadius: "24px",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
  },
  emptyStateIcon: {
    fontSize: "64px",
    marginBottom: "16px",
    opacity: 0.5,
  },
  dialogPaper: {
    borderRadius: "24px",
    maxWidth: 500,
    width: "100%",
    overflow: "hidden",
  },
  dialogTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#1a1a2e",
    padding: "24px 24px 8px",
  },
  dialogContent: {
    padding: "16px 24px 8px",
  },
  inputField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: "#f8fafc",
      transition: "all 0.2s ease",
      "& fieldset": { 
        borderColor: "#e2e8f0",
        borderWidth: "2px",
      },
      "&:hover fieldset": { 
        borderColor: "#94a3b8",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0d47a1",
        borderWidth: "2px",
      },
    },
  },
  formCard: {
    padding: "24px",
    borderRadius: "20px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.02)",
  },
  formLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#475569",
    mb: 1,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  dialogActions: {
    padding: "16px 24px 24px",
    gap: "12px",
  },
  cancelButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "10px 24px",
    color: "#64748b",
    border: "2px solid #e2e8f0",
    "&:hover": {
      backgroundColor: "#f1f5f9",
      borderColor: "#cbd5e1",
    },
  },
  saveButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "12px",
    padding: "10px 28px",
    background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
    boxShadow: "0 8px 20px rgba(13, 71, 161, 0.3)",
    transition: "all 0.3s ease",
    "&:hover:not(:disabled)": {
      background: "linear-gradient(135deg, #0a3d91 0%, #0d47a1 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 28px rgba(13, 71, 161, 0.4)",
    },
    "&:disabled": {
      opacity: 0.6,
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  chip: {
    backgroundColor: "rgba(13, 71, 161, 0.1)",
    color: "#0d47a1",
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: "8px",
    height: "24px",
  },
};

export default function CacheSettings() {
  const [key, setKey] = useState("");
  const [duration, setDuration] = useState("");
  const [description, setDescription] = useState("");
  const [settings, setSettings] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'update'
  const navigate = useNavigate();

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/getcache`);
      setSettings(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch settings");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchSettings();
  }, []);

  const openCreateDialog = () => {
    setDialogMode("create");
    setKey("");
    setDuration("");
    setDescription("");
    setSelectedKey("");
    setError(null);
    setDialogOpen(true);
  };

  const openUpdateDialog = (item) => {
    setDialogMode("update");
    setKey(item.key);
    setDuration(String(item.duration_ms || ""));
    setDescription(item.description || "");
    setSelectedKey(item.key);
    setError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setKey("");
    setDuration("");
    setDescription("");
    setSelectedKey("");
    setError(null);
  };

  const handleCreate = async () => {
    if (!key || !duration) {
      setError("Key and Duration are required");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await axios.post(`/cache`, {
        key,
        duration_ms: Number(duration),
        description,
      });
      await fetchSettings();
      closeDialog();
    } catch (err) {
      setError(err.response?.data?.error || "Create failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!duration) {
      setError("Duration is required");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await axios.put(`/put/${selectedKey}`, {
        duration_ms: Number(duration),
        description,
      });
      await fetchSettings();
      closeDialog();
    } catch (err) {
      setError(err.response?.data?.error || "Update failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (keyToDelete) => {
    if (!window.confirm("Are you sure you want to delete this cache setting?"))
      return;
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`/delete/${keyToDelete}`);
      await fetchSettings();
      if (selectedKey === keyToDelete) closeDialog();
    } catch (err) {
      setError(err.response?.data?.error || "Delete failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogSubmit = () => {
    if (dialogMode === "create") handleCreate();
    else handleUpdate();
  };

  // Format duration for display
  const formatDuration = (ms) => {
    if (!ms) return "0 ms";
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)} min`;
    if (ms < 86400000) return `${(ms / 3600000).toFixed(1)} hours`;
    return `${(ms / 86400000).toFixed(1)} days`;
  };

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <IconButton
              onClick={() => navigate(-1)}
              size="medium"
              sx={styles.backButton}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.title}>Cache Settings</Typography>
              <Typography sx={styles.subtitle}>
                Manage cache keys, durations and descriptions
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Add new cache setting" arrow TransitionComponent={Zoom}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={openCreateDialog}
              sx={styles.addButton}
            >
              Add Cache
            </Button>
          </Tooltip>
        </Box>

        {/* Error Alert */}
        {error && !dialogOpen && (
          <Fade in={!!error}>
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: "12px" }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Section Title with Count */}
        <Box sx={styles.sectionTitle}>
          <span>Existing Cache Settings</span>
          <Chip 
            label={`${settings.length} ${settings.length === 1 ? 'item' : 'items'}`}
            sx={styles.chip}
          />
        </Box>

        {/* Loading State */}
        {isLoading && settings.length === 0 ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={60} thickness={4} sx={{ color: "#0d47a1" }} />
          </Box>
        ) : settings.length === 0 ? (
          /* Empty State */
          <Fade in={true}>
            <Box sx={styles.emptyState}>
              <Box sx={styles.emptyStateIcon}>⚡</Box>
              <Typography sx={{ color: "#64748b", fontWeight: 600, fontSize: "1.25rem" }}>
                No cache settings yet
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: "1rem", mt: 1, mb: 3 }}>
                Click “Add Cache” to create your first cache setting.
              </Typography>
              <Button
                variant="contained"
                onClick={openCreateDialog}
                startIcon={<AddIcon />}
                sx={styles.addButton}
              >
                Add Cache
              </Button>
            </Box>
          </Fade>
        ) : (
          /* Card Grid - 4 columns */
          <Grid container spacing={3}>
            {settings.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                  <Card sx={styles.card} elevation={0}>
                    <CardContent sx={styles.cardContent}>
                      {/* Key with Icon */}
                      <Typography sx={styles.cardKey}>
                        <KeyIcon sx={styles.cardKeyIcon} />
                        {item.key}
                      </Typography>

                      {/* Duration Card */}
                      <Box sx={styles.cardDetail}>
                        <ScheduleIcon sx={styles.cardDetailIcon} />
                        <Box sx={styles.cardDetailContent}>
                          <Typography sx={styles.cardDetailLabel}>
                            Duration
                          </Typography>
                          <Typography sx={styles.cardDetailValue}>
                            {formatDuration(item.duration_ms)}
                          </Typography>
                        </Box>
                        <Chip 
                          label={`${item.duration_ms} ms`}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(13, 71, 161, 0.1)",
                            color: "#0d47a1",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>

                      {/* Description (if exists) */}
                      {item.description && (
                        <Box sx={styles.cardDescription}>
                          <Typography sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <DescriptionIcon sx={{ fontSize: "16px", color: "#64748b" }} />
                            <span style={{ fontWeight: 600, color: "#475569" }}>Description</span>
                          </Typography>
                          <Typography sx={{ color: "#1e293b", fontSize: "0.9rem" }}>
                            {item.description}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>

                    {/* Card Actions */}
                    <CardActions sx={styles.cardActions}>
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => openUpdateDialog(item)}
                        disabled={isLoading}
                        sx={styles.editButton}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<DeleteOutlinedIcon />}
                        onClick={() => handleDelete(item.key)}
                        disabled={isLoading}
                        sx={styles.deleteButton}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Create / Update Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        PaperProps={{ sx: styles.dialogPaper }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle sx={styles.dialogTitle}>
          {dialogMode === "create" ? "Create Cache Setting" : "Update Cache Setting"}
        </DialogTitle>

        <DialogContent sx={styles.dialogContent}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: "12px" }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Box sx={styles.formCard}>
            <Grid container spacing={3}>
              {/* Key Field */}
              <Grid item xs={12}>
                <Box>
                  <Typography component="label" sx={styles.formLabel}>
                    <KeyIcon sx={{ fontSize: "18px" }} />
                    Cache Key
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. user_profile, product_listing"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    disabled={dialogMode === "update"}
                    sx={styles.inputField}
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              </Grid>

              {/* Duration Field */}
              <Grid item xs={12}>
                <Box>
                  <Typography component="label" sx={styles.formLabel}>
                    <ScheduleIcon sx={{ fontSize: "18px" }} />
                    Duration (milliseconds)
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. 60000 (1 minute), 3600000 (1 hour)"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    type="number"
                    sx={styles.inputField}
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              </Grid>

              {/* Description Field */}
              <Grid item xs={12}>
                <Box>
                  <Typography component="label" sx={styles.formLabel}>
                    <DescriptionIcon sx={{ fontSize: "18px" }} />
                    Description (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. User profile data, expires in 5 minutes"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={3}
                    sx={styles.inputField}
                    variant="outlined"
                    size="medium"
                  />
                </Box>
              </Grid>

              {/* Helper Text */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 1 }}>
                  <span style={{ fontWeight: 600 }}>💡 Note:</span>
                  Key must be unique. Duration defines how long the cache should be stored.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={closeDialog}
            sx={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogSubmit}
            disabled={isLoading || (dialogMode === "create" ? !key || !duration : !duration)}
            sx={styles.saveButton}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              dialogMode === "create" ? "Create Cache" : "Update Cache"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}